import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Clock, Thermometer, CircleGauge, Waves, Droplet, Flame,
    AlertTriangle, LineChart, TrendingUp, Inbox,
    ChevronDown, RefreshCw, Loader2, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// ============================================
// ✅ Карта иконок и цветов для групп показаний
// ============================================
const GROUP_ICONS = {
    'Время и дата': Clock,
    'Температура': Thermometer,
    'Давление': CircleGauge,
    'Объемный расход': Droplet,
    'Массовый расход': Waves,
    'Тепловая энергия': Flame,
    'Нештатные ситуации': AlertTriangle,
    'Объем': Droplet,
    'Масса': Waves,
    'Энергия': Flame,
};

const GROUP_COLORS = {
    'Время и дата': 'border-blue-500/30 text-blue-400',
    'Температура': 'border-red-500/30 text-red-400',
    'Давление': 'border-cyan-500/30 text-cyan-400',
    'Объемный расход': 'border-purple-500/30 text-purple-400',
    'Массовый расход': 'border-indigo-500/30 text-indigo-400',
    'Тепловая энергия': 'border-orange-500/30 text-orange-400',
    'Нештатные ситуации': 'border-yellow-500/30 text-yellow-400',
    'Объем': 'border-teal-500/30 text-teal-400',
    'Масса': 'border-indigo-500/30 text-indigo-400',
    'Энергия': 'border-orange-500/30 text-orange-400',
};

// ============================================
// ✅ Утилиты форматирования
// ============================================
const formatValue = (val, decimals = 3) => {
    if (val === null || val === undefined) return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === -1) return 'НС';
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

const convertPressure = (val, deviceName) => {
    if (val === null || val === undefined) return { value: '—', unit: 'МПа' };
    const num = parseFloat(val);
    if (isNaN(num)) return { value: '—', unit: 'МПа' };
    if (num === -1) return { value: 'НС', unit: 'МПа' };
    
    if (deviceName === 'dev_350' || deviceName === 'dev_351') {
        return {
            value: (num * 101.9744288922).toLocaleString('ru-RU', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
            unit: 'метр вод. столба'
        };
    }
    if (deviceName === 'dev_352' || deviceName === 'dev_353') {
        return {
            value: (num * 9.86923).toLocaleString('ru-RU', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
            unit: 'атм.'
        };
    }
    return {
        value: num.toLocaleString('ru-RU', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
        unit: 'МПа'
    };
};

const switchConvert = (bitPosition, switchValue) => {
    if (switchValue === null || switchValue === undefined) return false;
    const num = parseInt(switchValue);
    if (isNaN(num)) return false;
    return (num & Math.pow(2, bitPosition)) === Math.pow(2, bitPosition);
};

// ============================================
// ✅ Функции обработки нештатных ситуаций
// ============================================
const addTemperatureNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 7 && (errByte & 0x01)) addNS(`АВАРИЯ — температура ${name} вне диапазона`);
    else if (idx === 8 && (errByte & 0x01)) addNS('АВАРИЯ — температура холодной воды вне диапазона');
    else if (idx >= 9 && idx <= 12) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность температуры ${name}`);
        if (errByte & 0x02) addNS(`АВАРИЯ — разность температуры ${name} вне диапазона`);
    } else if (idx === 13 && (errByte & 0x01)) addNS('ПРЕДУПРЕЖДЕНИЕ — температура окр. среды вне диапазона');
};

const addDavlenieNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 7 && (errByte & 0x01)) addNS(`ПРЕДУПРЕЖДЕНИЕ — давление ${name} вне диапазона, использовано значение при НС`);
    else if (idx === 8 && (errByte & 0x01)) addNS('ПРЕДУПРЕЖДЕНИЕ — давление холодной воды вне диапазона');
    else if (idx >= 9 && idx < 13) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность давления ${name}`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная разность давления ${name}`);
    }
};

const addVolumeNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 8) {
        if (errByte & 0x01) addNS(`ПРЕДУПРЕЖДЕНИЕ — расход ${name} выше максимального порога`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — расход ${name} ниже переходного порога`);
        if (errByte & 0x04) addNS(`ПРЕДУПРЕЖДЕНИЕ — расход ${name} ниже минимального порога`);
        if (errByte & 0x08) addNS(`АВАРИЯ — расход ${name} выше максимального порога`);
        if (errByte & 0x10) addNS(`АВАРИЯ — расход ${name} ниже минимального порога`);
    } else if (idx >= 9 && idx < 13) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность объемного расхода ${name}`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная разность объемного расхода ${name}`);
        if (errByte & 0x04) addNS(`АВАРИЯ — отрицательная разность объемного расхода ${name}`);
    }
};

const addMassaNS = (errByte, idx, name, addNS) => {
    if (idx >= 0 && idx <= 7 && (errByte & 0x01)) addNS(`АВАРИЯ — невозможно рассчитать массовый расход ${name}`);
    else if (idx === 8 && (errByte & 0x01)) addNS('АВАРИЯ — невозможно рассчитать массу холодной воды Gmx');
    else if (idx >= 9 && idx < 13) {
        if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать разность массового расхода ${name}`);
        if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная разность массового расхода ${name}`);
        if (errByte & 0x04) addNS(`АВАРИЯ — отрицательная разность массового расхода ${name}`);
    }
};

const addEnergiNS = (errByte, idx, name, addNS) => {
    if (errByte & 0x01) addNS(`АВАРИЯ — невозможно рассчитать энергию теплоносителя ${name}`);
    if (errByte & 0x02) addNS(`ПРЕДУПРЕЖДЕНИЕ — отрицательная энергия теплоносителя ${name}`);
    if (errByte & 0x04) addNS(`АВАРИЯ — отрицательная энергия теплоносителя ${name}`);
};

const generateNSItems = (tekyshie) => {
    const nsItems = [];
    const addNS = (message) => nsItems.push({ name: message, value: '', unit: '', isNS: true });
    const nsTemperatura = parseInt(tekyshie.NS_temperatura) || 0;
    const nsDavlenie = parseInt(tekyshie.NS_davlenie) || 0;
    const nsRasxod = parseInt(tekyshie.NS_rasxod) || 0;
    const nsEnergi = parseInt(tekyshie.NS_energi_pitanie) || 0;
    const actionsOnNS = parseInt(tekyshie.ActionsOnNS) || 0;
    const actionsOnSwitchOff = parseInt(tekyshie.ActionsOnSwitchOffPitanie) || 0;
    const workFunction = parseInt(tekyshie.work_function) || 0;

    for (let i = 0; i < 4; i++) {
        if ((nsTemperatura & Math.pow(2, i)) === Math.pow(2, i)) {
            if (actionsOnNS === 0) addNS(`АВАРИЯ: Температура датчика t${i + 1} вне диапазона`);
            else addNS(`УВЕДОМЛЕНИЕ: Температура датчика t${i + 1} вне диапазона, использовано значение t${i + 1}нс`);
        }
    }
    if ((nsTemperatura & 16) === 16) {
        if (actionsOnNS === 0) addNS('АВАРИЯ: Температура холодной воды (tx) вне диапазона');
        else addNS('УВЕДОМЛЕНИЕ: Температура холодной воды (tx) вне диапазона, использовано значение НС');
    }
    if ((nsTemperatura & 32) === 32) addNS('АВАРИЯ: Разность температур (dto) меньше 1 град.');
    if ((nsTemperatura & 64) === 64) addNS('АВАРИЯ: Разность температур (dtg) меньше 1 град.');
    if ((nsTemperatura & 128) === 128) {
        if (workFunction === 1 || workFunction === 2) addNS('УВЕДОМЛЕНИЕ: Температура окружающего воздуха (tok) вне диапазона');
        if (workFunction === 3) addNS('АВАРИЯ: Температура окружающего воздуха (tok) вне диапазона');
    }
    for (let i = 0; i < 4; i++) {
        if ((nsDavlenie & Math.pow(2, i)) === Math.pow(2, i)) {
            addNS(`УВЕДОМЛЕНИЕ: Давление датчика P${i + 1} вне диапазона, использовано значение P${i + 1}нс`);
        }
    }
    if ((nsDavlenie & 16) === 16) addNS('УВЕДОМЛЕНИЕ: Давление холодной воды (Px) вне диапазона, использовано значение НС');
    if ((nsDavlenie & 32) === 32) addNS('УВЕДОМЛЕНИЕ: Отрицательная разность давлений (dPo)');
    if ((nsDavlenie & 64) === 64) addNS('УВЕДОМЛЕНИЕ: Отрицательная разность давлений (dPg)');
    
    for (let i = 0; i < 4; i++) {
        if ((nsRasxod & Math.pow(2, i)) === Math.pow(2, i)) addNS(`УВЕДОМЛЕНИЕ: Расход датчика Go${i + 1} вне диапазона`);
    }
    if ((nsRasxod & 16) === 16) addNS('УВЕДОМЛЕНИЕ: Расход холодной воды (Gox) вне диапазона');
    if ((nsRasxod & 32) === 32) addNS('АВАРИЯ: Расход (Goo) вне диапазона');
    if ((nsRasxod & 64) === 64) addNS('АВАРИЯ: Расход (Gog) вне диапазона');
    if ((nsRasxod & 128) === 128) addNS('УВЕДОМЛЕНИЕ: Расход (Go) меньше переходного (Gper)');
    
    if ((nsEnergi & 1) === 1) addNS('АВАРИЯ: Тепловая энергия Qo вне диапазона');
    if ((nsEnergi & 2) === 2) addNS('АВАРИЯ: Тепловая энергия Qg вне диапазона');
    if ((nsEnergi & 16) === 16) {
        if (actionsOnSwitchOff === 1) addNS('АВАРИЯ: Внешнее питание отключено');
        if (actionsOnSwitchOff === 2) addNS('ВНИМАНИЕ: Внешнее питание отключено');
    }
    if ((nsEnergi & 32) === 32) addNS('ВНИМАНИЕ: Перезапуск ПО');
    
    return nsItems;
};

// ============================================
// ✅ Построение групп показаний
// ============================================
const buildTekyshieGroups = (tekyshie, deviceName) => {
    const groups = {};
    const addToGroup = (groupName, name, value, unit) => {
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push({ name, value, unit, isNS: value === 'НС' });
    };

    const dateTime = `${tekyshie.date || '—'} ${tekyshie.time || ''}`;
    addToGroup('Время и дата', 'Показания на', dateTime, '');

    if (switchConvert(0, tekyshie.switch_time)) addToGroup('Время и дата', 'Наработка (ВНoт)', formatValue(tekyshie.time_work), 'мин');
    if (switchConvert(1, tekyshie.switch_time)) addToGroup('Время и дата', 'Наработка (ВНgт)', formatValue(tekyshie.energi_time_work_1), 'мин');
    if (switchConvert(0, tekyshie.switch_time)) addToGroup('Время и дата', 'Простой (ВПoт)', formatValue(tekyshie.time_NOT_work), 'мин');
    if (switchConvert(1, tekyshie.switch_time)) addToGroup('Время и дата', 'Простой (ВПgт)', formatValue(tekyshie.energi_time_NOT_work_1), 'мин');

    if (switchConvert(0, tekyshie.switch_temperatura)) addToGroup('Температура', 't1', formatValue(tekyshie.t1), '°C');
    if (switchConvert(1, tekyshie.switch_temperatura)) addToGroup('Температура', 't2', formatValue(tekyshie.t2), '°C');
    if (switchConvert(2, tekyshie.switch_temperatura)) addToGroup('Температура', 't3', formatValue(tekyshie.t3), '°C');
    if (switchConvert(3, tekyshie.switch_temperatura)) addToGroup('Температура', 't4', formatValue(tekyshie.t4), '°C');
    if (switchConvert(4, tekyshie.switch_temperatura)) addToGroup('Температура', 'tx', formatValue(tekyshie.tx), '°C');
    if (switchConvert(5, tekyshie.switch_temperatura)) addToGroup('Температура', 'dto', formatValue(tekyshie.dto), '°C');
    if (switchConvert(6, tekyshie.switch_temperatura)) addToGroup('Температура', 'dtg', formatValue(tekyshie.dtg), '°C');
    if (switchConvert(7, tekyshie.switch_temperatura)) addToGroup('Температура', 'tok', formatValue(tekyshie.tok), '°C');

    if (switchConvert(0, tekyshie.switch_davlenie)) { const { value, unit } = convertPressure(tekyshie.P1, deviceName); addToGroup('Давление', 'P1', value, unit); }
    if (switchConvert(1, tekyshie.switch_davlenie)) { const { value, unit } = convertPressure(tekyshie.P2, deviceName); addToGroup('Давление', 'P2', value, unit); }
    if (switchConvert(2, tekyshie.switch_davlenie)) { const { value, unit } = convertPressure(tekyshie.P3, deviceName); addToGroup('Давление', 'P3', value, unit); }
    if (switchConvert(3, tekyshie.switch_davlenie)) { const { value, unit } = convertPressure(tekyshie.P4, deviceName); addToGroup('Давление', 'P4', value, unit); }
    if (switchConvert(4, tekyshie.switch_davlenie)) { const { value, unit } = convertPressure(tekyshie.Px, deviceName); addToGroup('Давление', 'Px', value, unit); }
    if (switchConvert(5, tekyshie.switch_davlenie)) { const { value, unit } = convertPressure(tekyshie.dPo, deviceName); addToGroup('Давление', 'dPo', value, unit); }
    if (switchConvert(6, tekyshie.switch_davlenie)) { const { value, unit } = convertPressure(tekyshie.dPg, deviceName); addToGroup('Давление', 'dPg', value, unit); }

    if (switchConvert(0, tekyshie.switch_rasxod)) addToGroup('Объемный расход', 'Go1', formatValue(tekyshie.Go1), 'м³/ч');
    if (switchConvert(1, tekyshie.switch_rasxod)) addToGroup('Объемный расход', 'Go2', formatValue(tekyshie.Go2), 'м³/ч');
    if (switchConvert(2, tekyshie.switch_rasxod)) addToGroup('Объемный расход', 'Go3', formatValue(tekyshie.Go3), 'м³/ч');
    if (switchConvert(3, tekyshie.switch_rasxod)) addToGroup('Объемный расход', 'Go4', formatValue(tekyshie.Go4), 'м³/ч');
    if (switchConvert(4, tekyshie.switch_rasxod)) addToGroup('Объемный расход', 'Gox', formatValue(tekyshie.Gox), 'м³/ч');
    if (switchConvert(5, tekyshie.switch_rasxod)) addToGroup('Объемный расход', 'Goo', formatValue(tekyshie.Goo), 'м³/ч');
    if (switchConvert(6, tekyshie.switch_rasxod)) addToGroup('Объемный расход', 'Gog', formatValue(tekyshie.Gog), 'м³/ч');

    if (switchConvert(0, tekyshie.switch_rasxod_massa)) addToGroup('Массовый расход', 'Gm1', formatValue(tekyshie.Gm1), 'т/ч');
    if (switchConvert(1, tekyshie.switch_rasxod_massa)) addToGroup('Массовый расход', 'Gm2', formatValue(tekyshie.Gm2), 'т/ч');
    if (switchConvert(2, tekyshie.switch_rasxod_massa)) addToGroup('Массовый расход', 'Gm3', formatValue(tekyshie.Gm3), 'т/ч');
    if (switchConvert(3, tekyshie.switch_rasxod_massa)) addToGroup('Массовый расход', 'Gm4', formatValue(tekyshie.Gm4), 'т/ч');
    if (switchConvert(4, tekyshie.switch_rasxod_massa)) addToGroup('Массовый расход', 'Gmx', formatValue(tekyshie.Gmx), 'т/ч');
    if (switchConvert(5, tekyshie.switch_rasxod_massa)) addToGroup('Массовый расход', 'Gmo', formatValue(tekyshie.Gmo), 'т/ч');
    if (switchConvert(6, tekyshie.switch_rasxod_massa)) addToGroup('Массовый расход', 'Gmg', formatValue(tekyshie.Gmg), 'т/ч');

    if (switchConvert(0, tekyshie.switch_energi)) addToGroup('Тепловая энергия', 'Qro', formatValue(tekyshie.Qro), 'Гкал/ч');
    if (switchConvert(1, tekyshie.switch_energi)) addToGroup('Тепловая энергия', 'Qrg', formatValue(tekyshie.Qrg), 'Гкал/ч');

    const nsItemsGenerated = generateNSItems(tekyshie);
    if (nsItemsGenerated.length > 0) groups['Нештатные ситуации'] = nsItemsGenerated;

    return Object.entries(groups).map(([name, items]) => ({ name, items }));
};

const buildNacoplenGroups = (summarnue) => {
    const groups = {};
    const addToGroup = (groupName, name, value, unit) => {
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push({ name, value, unit });
    };

    const dateTime = `${summarnue.date || '—'} ${summarnue.time || ''}`;
    addToGroup('Время и дата', 'Показания на', dateTime, '');

    if (switchConvert(0, summarnue.switch_time)) addToGroup('Время и дата', 'Наработка (ВНo)', formatValue(summarnue.time_work / 60), 'час');
    if (switchConvert(1, summarnue.switch_time)) addToGroup('Время и дата', 'Наработка (ВНg)', formatValue(summarnue.energi_time_work_in_min_1 / 60), 'час');
    if (switchConvert(0, summarnue.switch_time)) addToGroup('Время и дата', 'Простой (ВПo)', formatValue(summarnue.time_NOT_work / 60), 'час');
    if (switchConvert(1, summarnue.switch_time)) addToGroup('Время и дата', 'Простой (ВПg)', formatValue(summarnue.energi_time_NOT_work_in_min_1 / 60), 'час');

    if (switchConvert(0, summarnue.switch_rasxod)) addToGroup('Объем', 'V1', formatValue(summarnue.V1), 'м³');
    if (switchConvert(1, summarnue.switch_rasxod)) addToGroup('Объем', 'V2', formatValue(summarnue.V2), 'м³');
    if (switchConvert(2, summarnue.switch_rasxod)) addToGroup('Объем', 'V3', formatValue(summarnue.V3), 'м³');
    if (switchConvert(3, summarnue.switch_rasxod)) addToGroup('Объем', 'V4', formatValue(summarnue.V4), 'м³');
    if (switchConvert(4, summarnue.switch_rasxod)) addToGroup('Объем', 'Vx', formatValue(summarnue.Vx), 'м³');
    if (switchConvert(5, summarnue.switch_rasxod)) addToGroup('Объем', 'Vo', formatValue(summarnue.Vo), 'м³');
    if (switchConvert(6, summarnue.switch_rasxod)) addToGroup('Объем', 'Vg', formatValue(summarnue.Vg), 'м³');

    if (switchConvert(0, summarnue.switch_rasxod_massa)) addToGroup('Масса', 'M1', formatValue(summarnue.M1), 'т');
    if (switchConvert(1, summarnue.switch_rasxod_massa)) addToGroup('Масса', 'M2', formatValue(summarnue.M2), 'т');
    if (switchConvert(2, summarnue.switch_rasxod_massa)) addToGroup('Масса', 'M3', formatValue(summarnue.M3), 'т');
    if (switchConvert(3, summarnue.switch_rasxod_massa)) addToGroup('Масса', 'M4', formatValue(summarnue.M4), 'т');
    if (switchConvert(4, summarnue.switch_rasxod_massa)) addToGroup('Масса', 'Mx', formatValue(summarnue.Mx), 'т');
    if (switchConvert(5, summarnue.switch_rasxod_massa)) addToGroup('Масса', 'Mo', formatValue(summarnue.Mo), 'т');
    if (switchConvert(6, summarnue.switch_rasxod_massa)) addToGroup('Масса', 'Mg', formatValue(summarnue.Mg), 'т');

    if (switchConvert(0, summarnue.switch_energi)) addToGroup('Энергия', 'Qo', formatValue(summarnue.Qo), 'Гкал');
    if (switchConvert(1, summarnue.switch_energi)) addToGroup('Энергия', 'Qg', formatValue(summarnue.Qg), 'Гкал');

    return Object.entries(groups).map(([name, items]) => ({ name, items }));
};

// ============================================
// ✅ Компоненты отображения
// ============================================
const ReadingsGroup = ({ group }) => {
    const [isOpen, setIsOpen] = useState(true);
    const isNSGroup = group.name === 'Нештатные ситуации';
    const IconComponent = GROUP_ICONS[group.name] || Clock;
    const colorClass = GROUP_COLORS[group.name] || 'border-slate-700 text-slate-400';
    const [borderColor, iconColor] = colorClass.split(' ');

    return (
        <div className={`bg-slate-900 rounded-lg border ${borderColor} overflow-hidden mb-3`}>
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800/50 transition">
                <div className="flex items-center gap-2">
                    <IconComponent className={`w-4 h-4 ${iconColor}`} />
                    <h3 className="text-white font-semibold text-xs sm:text-sm">{group.name}</h3>
                    <span className="text-slate-500 text-[10px] sm:text-xs">({group.items.length})</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="border-t border-slate-700">
                    {group.items.length === 0 ? (
                        <div className="px-3 py-4 text-center text-slate-500 text-xs">
                            {isNSGroup ? (
                                <div className="flex flex-col items-center gap-1">
                                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                                    <span>Нештатных ситуаций нет</span>
                                </div>
                            ) : 'Нет данных'}
                        </div>
                    ) : isNSGroup ? (
                        <div className="divide-y divide-slate-700/50">
                            {group.items.map((item, idx) => (
                                <div key={idx} className="px-3 py-2 text-xs bg-red-500/10 hover:bg-red-500/20 transition flex items-start gap-2">
                                    <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-red-200 font-medium flex-1">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-slate-800/50 text-[10px] sm:text-xs text-slate-400 font-semibold">
                                <div className="col-span-6">Параметр</div>
                                <div className="col-span-4 text-center">Значение</div>
                                <div className="col-span-2 text-right">Ед. изм.</div>
                            </div>
                            <div className="divide-y divide-slate-700/50">
                                {group.items.map((item, idx) => (
                                    <div key={idx} className={`grid grid-cols-12 gap-2 px-3 py-1.5 text-xs hover:bg-slate-800/30 transition ${item.isNS ? 'bg-red-500/10' : ''}`}>
                                        <div className="col-span-6 text-slate-300 truncate" title={item.name}>{item.name}</div>
                                        <div className={`col-span-4 text-center font-mono ${item.isNS ? 'text-red-400 font-bold' : 'text-white'}`}>{item.value}</div>
                                        <div className="col-span-2 text-right text-slate-400 text-[10px]">{item.unit}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const TekyshieView = ({ tekyshie, deviceName }) => {
    if (!tekyshie) return <div className="text-center py-6 text-slate-400 text-xs"><Inbox className="w-8 h-8 mx-auto mb-2 text-slate-600" /><p>Нет данных</p></div>;
    const groups = buildTekyshieGroups(tekyshie, deviceName);
    return <div className="space-y-2">{groups.map((group, idx) => <ReadingsGroup key={idx} group={group} />)}</div>;
};

const NacoplenView = ({ summarnue }) => {
    if (!summarnue) return <div className="text-center py-6 text-slate-400 text-xs"><Inbox className="w-8 h-8 mx-auto mb-2 text-slate-600" /><p>Нет данных</p></div>;
    const groups = buildNacoplenGroups(summarnue);
    return <div className="space-y-2">{groups.map((group, idx) => <ReadingsGroup key={idx} group={group} />)}</div>;
};

// ============================================
// ✅ Главный компонент попапа для Аксиома-01
// ============================================
const Aksioma01PopupContent = ({ device }) => {
    const [activeSubTab, setActiveSubTab] = useState('tekyshie');
    const [readings, setReadings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadReadings = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}/api/devices/aksioma01/${device.name}/readings`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setReadings(response.data);
        } catch (err) {
            console.error('Ошибка загрузки показаний:', err);
            setError('Не удалось загрузить показания');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadReadings();
    }, [device.name]);

    if (isLoading) return <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin h-6 w-6 text-blue-500" /></div>;
    if (error) return <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-3 py-2 rounded-lg text-xs">{error}</div>;
    if (!readings) return <div className="text-center py-6 text-slate-400 text-xs"><Inbox className="w-8 h-8 mx-auto mb-2 text-slate-600" />Нет данных показаний</div>;

    return (
        <div className="space-y-3">
            <div className="flex border-b border-slate-700">
                <button
                    onClick={() => setActiveSubTab('tekyshie')}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition flex items-center justify-center gap-1.5 ${activeSubTab === 'tekyshie' ? 'bg-blue-600/20 text-blue-400 border-b-2 border-blue-500' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'}`}
                >
                    <LineChart className="w-3 h-3" /> Текущие
                </button>
                <button
                    onClick={() => setActiveSubTab('nacoplen')}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition flex items-center justify-center gap-1.5 ${activeSubTab === 'nacoplen' ? 'bg-blue-600/20 text-blue-400 border-b-2 border-blue-500' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'}`}
                >
                    <TrendingUp className="w-3 h-3" /> Накопленные
                </button>
                <button onClick={loadReadings} className="px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-700/50 transition" title="Обновить">
                    <RefreshCw className="w-3 h-3" />
                </button>
            </div>

            <div className="max-h-[450px] overflow-y-auto custom-scrollbar pr-1">
                {activeSubTab === 'tekyshie' ? (
                    <TekyshieView tekyshie={readings.tekyshie} deviceName={readings.deviceName || device.name} />
                ) : (
                    <NacoplenView summarnue={readings.summarnue} />
                )}
            </div>
        </div>
    );
};

export default Aksioma01PopupContent;